import type { SVGProps } from 'react'

/**
 * Iconsatz für das Navigations-Dock.
 *
 * Alle auf demselben 16er-Raster, alle konturbasiert mit gleicher Strichstärke,
 * alle ohne Füllung. Ein gemischter Satz aus Kontur- und Flächen-Icons fällt in
 * einer Reihe sofort unangenehm auf.
 *
 * `currentColor` durchgehend, damit der aktive Zustand allein über die
 * Textfarbe der Kachel geregelt wird.
 */

type IconProps = SVGProps<SVGSVGElement>

function Icon({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const IconOverview = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2" y="2" width="5" height="5" rx="1.2" />
    <rect x="9" y="2" width="5" height="5" rx="1.2" />
    <rect x="2" y="9" width="5" height="5" rx="1.2" />
    <rect x="9" y="9" width="5" height="5" rx="1.2" />
  </Icon>
)

export const IconSpaces = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 1.8 14.2 5 8 8.2 1.8 5 8 1.8Z" />
    <path d="M1.8 8.4 8 11.6l6.2-3.2" />
    <path d="M1.8 11.6 8 14.8l6.2-3.2" />
  </Icon>
)

export const IconDocuments = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 1.8H4.4A1.4 1.4 0 0 0 3 3.2v9.6a1.4 1.4 0 0 0 1.4 1.4h7.2a1.4 1.4 0 0 0 1.4-1.4V5.8L9 1.8Z" />
    <path d="M9 1.8v4h4" />
    <path d="M5.8 9h4.4M5.8 11.4h3" />
  </Icon>
)

export const IconProjects = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3.4 14.2V2.2" />
    <path d="M3.4 2.6h8.2l-1.8 2.6 1.8 2.6H3.4" />
  </Icon>
)

export const IconParts = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 1.9 13.6 5v6L8 14.1 2.4 11V5L8 1.9Z" />
    <path d="M2.6 5.1 8 8.2l5.4-3.1" />
    <path d="M8 8.2v5.9" />
  </Icon>
)

export const IconChanges = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="4.2" cy="3.6" r="1.6" />
    <circle cx="4.2" cy="12.4" r="1.6" />
    <circle cx="11.8" cy="8" r="1.6" />
    <path d="M4.2 5.2v5.6" />
    <path d="M5.7 4.2c2.4.3 4.1 1.6 4.6 3.3M10.3 8.7c-.6 1.7-2.3 2.9-4.6 3.2" />
  </Icon>
)

export const IconEquipment = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.4 11.4a5.6 5.6 0 1 1 11.2 0" />
    <path d="M8 11.4 10.7 7" />
    <path d="M2.4 11.4h11.2" />
  </Icon>
)

export const IconPeople = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="6.2" cy="5.4" r="2.4" />
    <path d="M1.9 13.4c0-2.2 1.9-3.8 4.3-3.8s4.3 1.6 4.3 3.8" />
    <path d="M11 3.4a2.3 2.3 0 0 1 0 4.3M12.1 9.9c1.3.5 2.1 1.6 2.1 3.1" />
  </Icon>
)

export const IconAudit = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 3.4a4.6 4.6 0 1 1-4.4 6" />
    <path d="M3.2 3.2v2.6h2.6" />
    <path d="M8 5.9V8l1.6 1.1" />
  </Icon>
)

export const IconStyleguide = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 1.9c3.4 0 6.1 2.5 6.1 5.6 0 1.8-1.4 2.7-2.8 2.7h-1.1c-1 0-1.7.7-1.7 1.6 0 .4.2.8.4 1.1.2.3.3.5.3.8 0 .5-.4.8-1.2.8-3.4 0-6.1-2.8-6.1-6.3S4.6 1.9 8 1.9Z" />
    <circle cx="5.5" cy="6.6" r=".9" />
    <circle cx="9.4" cy="5.2" r=".9" />
  </Icon>
)

export const IconSun = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="3.1" />
    <path d="M8 1.2v1.5M8 13.3v1.5M14.8 8h-1.5M2.7 8H1.2M12.8 3.2l-1.1 1.1M4.3 11.7l-1.1 1.1M12.8 12.8l-1.1-1.1M4.3 4.3 3.2 3.2" />
  </Icon>
)

export const IconMoon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.4 9.5A5.7 5.7 0 0 1 6.5 2.6a5.7 5.7 0 1 0 6.9 6.9Z" />
  </Icon>
)

export const IconSignOut = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.2 14.1H3.6a1.4 1.4 0 0 1-1.4-1.4V3.3a1.4 1.4 0 0 1 1.4-1.4h2.6" />
    <path d="M10.4 11.2 13.6 8l-3.2-3.2" />
    <path d="M13.6 8H6.2" />
  </Icon>
)

/* -------------------------------------------------------------------------
   Simulator

   Jedes Icon zeigt, was die Seite TUT, nicht wovon sie handelt. Ein Zahnrad für
   „Antriebe" wäre bequem und in einer Reihe von sechzehn Kacheln wertlos — man
   erkennt es nicht wieder. Deshalb: das Moment als Bogen mit Pfeil, die Kanäle
   als Kurve über einer Achse, das Modell als Gelenkkette.
   ------------------------------------------------------------------------- */

/** Modell — eine Gelenkkette aus Punkten und Gliedern. */
export const IconModel = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="2.6" r="1.3" />
    <path d="M8 3.9v3.4" />
    <circle cx="8" cy="8.6" r="1.3" />
    <path d="M8 9.9l-2.6 3.5M8 9.9l2.6 3.5" />
    <circle cx="4.6" cy="14" r="1" />
    <circle cx="11.4" cy="14" r="1" />
  </Icon>
)

/** Parameter — Schieberegler. */
export const IconParameters = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 4h12M2 8h12M2 12h12" />
    <circle cx="5.5" cy="4" r="1.5" />
    <circle cx="10" cy="8" r="1.5" />
    <circle cx="6.8" cy="12" r="1.5" />
  </Icon>
)

/** Merkmale — Schalter, einer davon aus. */
export const IconFeatures = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2" y="3" width="12" height="4.4" rx="2.2" />
    <circle cx="11.2" cy="5.2" r="1.1" />
    <rect x="2" y="8.6" width="12" height="4.4" rx="2.2" />
    <circle cx="4.8" cy="10.8" r="1.1" />
  </Icon>
)

/** Simulation — eine Kurve über der Zeitachse. */
export const IconChannels = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 13V3M2 13h12" />
    <path d="M2.6 10.5c2-6 3.4 3.2 5.2-.6 1.6-3.4 2.6 3.4 4.4 1.1" />
  </Icon>
)

/** Kanäle — mehrere Spuren übereinander, eine davon gewählt. */
export const IconSignals = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 4h3l1.4-2 1.6 4 1.4-2H14" />
    <path d="M2 9h3.4l1.6 2.6L8.6 9H14" />
    <path d="M2 13.6h12" opacity="0.45" />
  </Icon>
)

/** Analyse — Kennwerte als Balken. */
export const IconAnalysis = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 13V3M2 13h12" />
    <path d="M4.8 13V8.4M7.8 13V5M10.8 13V9.8" />
  </Icon>
)

/** Antriebe — Moment als Bogen mit Pfeil. */
export const IconDrives = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="2.1" />
    <path d="M8 2.4a5.6 5.6 0 0 1 4.9 8.3" />
    <path d="M11.2 10.2l1.7.5.5-1.7" />
    <path d="M8 13.6a5.6 5.6 0 0 1-4.9-8.3" />
  </Icon>
)

/** Recorder — Zeitschrieb. */
export const IconRecorder = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <circle cx="8" cy="8" r="2" />
  </Icon>
)

/** Freigang — Hüllvolumen. */
export const IconClearance = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 2.4v11.2" />
    <path d="M3 6.2a6 6 0 0 0 10 0" />
    <path d="M3 9.8a6 6 0 0 1 10 0" />
  </Icon>
)

/** Balance — Waage über einem Drehpunkt. */
export const IconBalance = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 2.6v10.8M4 13.4h8" />
    <path d="M3 5.4h10" />
    <path d="M3 5.4L1.6 9a2 2 0 0 0 2.8 0z" />
    <path d="M13 5.4L11.6 9a2 2 0 0 0 2.8 0z" />
  </Icon>
)

/** Stückliste — Zeilen mit Haken. */
export const IconPartsList = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 4h8M6 8h8M6 12h8" />
    <path d="M2 3.8l1 1 1.6-1.8M2 7.8l1 1 1.6-1.8M2 11.8l1 1 1.6-1.8" />
  </Icon>
)

/** PLM — ein Teil, das aus der Konstruktion zurückkommt: Würfel mit Rückpfeil. */
export const IconPlm = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 2.2 13.4 5v6L8 13.8 2.6 11V5z" />
    <path d="M2.6 5 8 7.8 13.4 5M8 7.8v6" />
  </Icon>
)

/** Dokumentation — aufgeschlagenes Buch. */
export const IconDocs = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 4.2C6.6 3 4.6 2.7 2.4 3v9.4c2.2-.3 4.2 0 5.6 1.2 1.4-1.2 3.4-1.5 5.6-1.2V3c-2.2-.3-4.2 0-5.6 1.2z" />
    <path d="M8 4.2v9.4" />
  </Icon>
)

/** Aufbau — Baugruppen. */
export const IconAssembly = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2.2" y="2.2" width="5" height="5" rx="1" />
    <rect x="8.8" y="2.2" width="5" height="5" rx="1" />
    <rect x="5.5" y="8.8" width="5" height="5" rx="1" />
    <path d="M4.7 7.2v1.6h6.6V7.2" />
  </Icon>
)

/** Architektur — Regelkreis. */
export const IconArchitecture = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2" y="6" width="4.4" height="4" rx="1" />
    <rect x="9.6" y="6" width="4.4" height="4" rx="1" />
    <path d="M6.4 8h3.2" />
    <path d="M11.8 10v2.6H4.2V10" />
    <path d="M5 11.4l-.8-1.4-.8 1.4" />
  </Icon>
)

/** Konzept — Zielscheibe mit Anforderung. */
export const IconConcept = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="5.6" />
    <circle cx="8" cy="8" r="2.4" />
    <path d="M8 2.4v2.2M8 11.4v2.2M2.4 8h2.2M11.4 8h2.2" />
  </Icon>
)

/**
 * Umsetzung — Balkenplan.
 *
 * Drei Balken auf einer Zeitachse und keine Fahne, kein Kalenderblatt, kein
 * Zahnrad: die Seite handelt von der Zeit, in der etwas entsteht, und ein
 * Balkenplan ist das Bild dafür.
 */
export const IconRoadmap = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2.6 3.4h7.2" />
    <path d="M4.6 8h8.8" />
    <path d="M2.6 12.6h5.6" />
    <path d="M2 2.2v11.6" />
  </Icon>
)

/**
 * P00 — Kurbel, Schubstange, Kniehebel.
 *
 * Die Vierstabkette selbst und nicht ein Bein: das Kapitel handelt vom
 * Kraftpfad, nicht von der Anatomie. Oben die Kurbelachse im Oberschenkel,
 * unten die Knieachse, dazwischen die Stange.
 */
export const IconKnee = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="4.4" cy="4.7" r="1.6" />
    <circle cx="11.7" cy="11.7" r="1.2" />
    <path d="M5.7 5.6l5.1 5.2" />
    <path d="M4.4 6.3v6.1" />
    <path d="M3 12.4h2.8" />
  </Icon>
)

/** Software — Klammern. */
export const IconSoftware = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 3C4 3 4.6 6.6 3 8c1.6 1.4 1 5 3 5" />
    <path d="M10 3c2 0 1.4 3.6 3 5-1.6 1.4-1 5-3 5" />
  </Icon>
)

/** Markt — Vergleichsbalken. */
export const IconMarket = (p: IconProps) => (
  <Icon {...p}>
    <path d="M2 12.6h12" />
    <rect x="2.6" y="7.4" width="2.8" height="5.2" rx="0.6" />
    <rect x="6.6" y="4" width="2.8" height="8.6" rx="0.6" />
    <rect x="10.6" y="9.2" width="2.8" height="3.4" rx="0.6" />
  </Icon>
)

/** Praxis — Frage und Antwort. */
export const IconPractice = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.4 10.2a1.6 1.6 0 0 1-1.6 1.6H5.6L2.6 14V4.2a1.6 1.6 0 0 1 1.6-1.6h7.6a1.6 1.6 0 0 1 1.6 1.6z" />
    <path d="M6.6 6.2a1.4 1.4 0 1 1 1.9 1.3v.9" />
    <path d="M8.5 9.9v.1" />
  </Icon>
)

/**
 * Modellstände — gestapelte Blätter mit einem Siegel.
 *
 * Das Siegel ist der Unterschied zu einer blossen Liste: ein festgeschriebener
 * Stand ist unveränderlich, und das ist die Eigenschaft, wegen der es die Seite
 * gibt.
 */
export const IconVersions = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="2.5" width="11" height="13" rx="1.5" />
    <path d="M6.5 6h5M6.5 9h5M6.5 12h3" />
    <circle cx="14" cy="14" r="3.2" fill="none" />
    <path d="M12.7 14l.9 1 1.7-1.9" />
  </Icon>
)

/**
 * Prüfstand — die Vorwärtsrechnung.
 *
 * Ein Pendel über einer Grundlinie: die Bewegung stellt sich ein, statt
 * vorgegeben zu sein. Das ist der ganze Unterschied zu den anderen Kacheln.
 */
export const IconTestbed = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 16.5h14" />
    <path d="M10 3.2v5.6" />
    <circle cx="10" cy="11.2" r="2.4" fill="none" />
    <path d="M5.4 5.2A6.4 6.4 0 0 0 4.4 8.6" />
    <path d="M14.6 5.2a6.4 6.4 0 0 1 1 3.4" />
  </Icon>
)

/**
 * Werkzeugkette — Stationen hintereinander.
 *
 * Drei Glieder mit zwei Übergängen dazwischen. Kein Zahnrad und kein
 * Werkzeugkasten: es geht um die Reihenfolge, nicht um das Werkzeug.
 */
export const IconChain = (p: IconProps) => (
  <Icon {...p}>
    <rect x="1.8" y="7" width="4.4" height="6" rx="1.2" />
    <rect x="7.8" y="7" width="4.4" height="6" rx="1.2" />
    <rect x="13.8" y="7" width="4.4" height="6" rx="1.2" />
    <path d="M6.2 10h1.6M12.2 10h1.6" />
  </Icon>
)
